import { Icon, Popover } from '@/components/ui';
import {
  isBulkJobActive,
  jobLabel,
  useBulkJobsStore,
  type BulkJobInfo,
} from '@/realtime/bulkJobsStore';

/**
 * Top-bar activity icon: visible while background jobs are known for this
 * session, with a badge counting the running ones and a popover listing each
 * job with a mini progress bar. Fed by bulkJobsStore (bulk operations today;
 * any future job type that lands in the store shows up automatically).
 */
export function JobsIndicator() {
  const jobs = useBulkJobsStore((s) => s.jobs);
  // ObjectIds are time-ordered — newest job first.
  const list = Object.values(jobs).sort((a, b) => (a.id < b.id ? 1 : -1));
  if (list.length === 0) return null;

  const runningCount = list.filter((j) => isBulkJobActive(j.status)).length;

  return (
    <Popover
      placement="bottom-end"
      trigger={
        <button
          type="button"
          className="ds-icon-btn"
          aria-label={
            runningCount > 0
              ? `Background jobs: ${runningCount} running`
              : 'Background jobs'
          }
          style={{ position: 'relative', width: 32, height: 32, padding: 0 }}
        >
          <Icon name={runningCount > 0 ? 'spinner' : 'refresh'} size={15} />
          {runningCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                minWidth: 14,
                height: 14,
                padding: '0 3px',
                borderRadius: 7,
                background: 'var(--brand)',
                color: '#fff',
                fontSize: 9.5,
                fontWeight: 600,
                lineHeight: '14px',
                textAlign: 'center',
              }}
            >
              {runningCount}
            </span>
          )}
        </button>
      }
    >
      <div style={{ width: 260, padding: 4 }}>
        <div
          style={{
            padding: '2px 6px 8px',
            fontSize: 11.5,
            fontWeight: 600,
            color: 'var(--text-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Background jobs
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </div>
      </div>
    </Popover>
  );
}

function JobRow({ job }: { job: BulkJobInfo }) {
  const active = isBulkJobActive(job.status);
  const pct = job.total > 0 ? Math.min(100, Math.round((job.processed / job.total) * 100)) : 0;
  const statusColor =
    job.status === 'failed' ? 'var(--red)' : active ? 'var(--brand)' : 'var(--green)';

  return (
    <div style={{ padding: '4px 6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 500, color: 'var(--text-1)' }}>
          {jobLabel(job)}
        </span>
        <span style={{ fontSize: 11, color: statusColor }}>
          {active ? `${pct}%` : job.status}
        </span>
      </div>
      <div
        style={{
          marginTop: 4,
          height: 4,
          borderRadius: 2,
          background: 'var(--border)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${active ? pct : 100}%`,
            height: '100%',
            borderRadius: 2,
            background: statusColor,
            transition: 'width 300ms ease',
          }}
        />
      </div>
      <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-3)' }}>
        {active
          ? `${job.processed.toLocaleString()} / ${job.total.toLocaleString()} processed`
          : job.jobType === 'import'
            ? `${(job.created ?? 0).toLocaleString()} created, ${(job.updated ?? 0).toLocaleString()} updated${(job.failed ?? 0) > 0 ? `, ${(job.failed ?? 0).toLocaleString()} errors` : ''}`
            : `${job.affected.toLocaleString()} of ${job.total.toLocaleString()} affected${job.skipped > 0 ? `, ${job.skipped.toLocaleString()} skipped` : ''}`}
      </div>
    </div>
  );
}
